// client/src/components/auth/Register.js - TRADOTTO
import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../layout/LanguageSelector';
import { sanitizeInput } from '../utils/sanitize'; // ✅ USA QUELLO CHE ESISTE


// Security utilities minime (solo per protezioni critiche)
const Security = {
  limits: new Map(),
  
  checkReg(email) {
    const k = email.toLowerCase(), now = Date.now();
    const l = this.limits.get(k) || { c: 0, t: now };
    if (now - l.t > 3600000) { this.limits.set(k, { c: 0, t: now }); return true; }
    return l.c < 3;
  },
  
  fail(email) {
    const k = email.toLowerCase(), l = this.limits.get(k) || { c: 0, t: Date.now() };
    l.c++; this.limits.set(k, l);
  },
  
  // Solo minacce CRITICHE
  threat(input) {
    if (!input || typeof input !== 'string') return false;
    return [/<script[^>]*>/i, /<iframe[^>]*>/i, /javascript\s*:/i, /on\w+\s*=/i].some(p => p.test(input));
  },
  
  // ✅ MANUAL sanitizeFormData usando il tuo sanitizeInput esistente
  sanitizeFormData(formData) {
    const sanitized = {};
    const excludeFields = ['password', 'confirmPassword'];
    
    for (const [key, value] of Object.entries(formData)) {
      if (typeof value === 'object' && value !== null) {
        // Handle nested objects like organizationAddress
        sanitized[key] = this.sanitizeFormData(value);
      } else if (typeof value === 'string' && !excludeFields.includes(key)) {
        sanitized[key] = sanitizeInput(value);
      } else {
        sanitized[key] = value;
      }
    }
    
    return sanitized;
  }
};

const Register = () => {

  const { t, i18n } = useTranslation();
  const currentLanguage = (i18n.language || 'it').toLowerCase().startsWith('it') ? 'it' : 'en';
  const [step, setStep] = useState(1);
  const [userType, setUserType] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    position: '',
    // Organization fields
    organizationName: '',
    organizationType: '',
    vatNumber: '',
    organizationAddress: {
      street: '',
      city: '',
      postcode: '',
      country: ''
    },
    organizationEmail: '',
    organizationPhone: '',
    acceptedTerms: false
  });
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [securityWarning, setSecurityWarning] = useState('');
  
  const lastSubmit = useRef(0);
  const navigate = useNavigate();
  const { login } = useAuth();

  const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);

  const onChange = e => {
    // Controllo minacce critiche durante typing
    if (Security.threat(e.target.value)) {
      setSecurityWarning(t('auth.forgotPassword.errors.invalidContent'));
      return;
    }
    
    // Reset warning se input OK
    if (securityWarning) setSecurityWarning('');
    
    // ORIGINALE: Mantiene tutta la logica esistente
    if (e.target.name.startsWith('address.')) {
      const addressField = e.target.name.split('.')[1];
      setFormData({
        ...formData,
        organizationAddress: {
          ...formData.organizationAddress,
          [addressField]: e.target.value
        }
      });
    } else if (e.target.type === 'checkbox') {
      setFormData({ ...formData, [e.target.name]: e.target.checked });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const onUserTypeSelect = type => {
    setUserType(type);
    setFormData({
      ...formData,
      organizationType: type === 'bank' ? 'bank' : 'company'
    });
    setStep(2);
  };

  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    
    // Rate limiting minimo
    if (!Security.checkReg(formData.email)) {
      return setError(t('auth.register.errors.tooManyAttempts'));
    }
    
    // Timing protection
    const now = Date.now();
    if (now - lastSubmit.current < 2000) {
      return setError(t('auth.register.errors.waitBefore'));
    }
    
    // Controllo minacce critiche finali
    const allValues = [
      formData.firstName, formData.lastName, formData.email, formData.phone,
      formData.position, formData.organizationName, formData.password,
      formData.organizationAddress.street,formData.vatNumber,formData.organizationAddress.city,
      formData.organizationAddress.postcode, formData.organizationAddress.country
    ];
    
    if (allValues.some(val => Security.threat(val))) {
      return setError(t('auth.register.errors.invalidContent'));
    }
    
    lastSubmit.current = now;
    
    // Password validation
    if (formData.password !== formData.confirmPassword) {
      return setError(t('auth.register.errors.passwordMismatch'));
    }
    
    if (formData.password.length < 10 || 
    !/[A-Z]/.test(formData.password) || 
    !/[a-z]/.test(formData.password) || 
    !/[0-9]/.test(formData.password) || 
    !/[^A-Za-z0-9]/.test(formData.password)) {
      return setError(t('auth.register.errors.passwordWeak'));
    }
    
    if (!formData.acceptedTerms) {
      return setError(t('auth.register.errors.acceptTermsRequired'));
    }

    
    setLoading(true);
    
    try {
      Security.fail(formData.email);
      
      // ✅ USA LA FUNZIONE LOCALE CHE FUNZIONA
      const sanitizedData = Security.sanitizeFormData(formData);
      
      const registrationData = {
        firstName: sanitizedData.firstName,
        lastName: sanitizedData.lastName,
        email: sanitizedData.email,
        password: formData.password, // Don't sanitize passwords
        phone: sanitizedData.phone,
        position: sanitizedData.position,
        role: userType === 'bank' ? 'bank' : 'company',
        organizationName: sanitizedData.organizationName,
        organizationType: sanitizedData.organizationType,
        vatNumber: sanitizedData.vatNumber,
        organizationAddress: sanitizedData.organizationAddress,
        organizationEmail: sanitizedData.organizationEmail || sanitizedData.email,
        organizationPhone: sanitizedData.organizationPhone || sanitizedData.phone,
        acceptedTerms: formData.acceptedTerms,
        preferredLanguage: selectedLanguage
      };
      
      const res = await axios.post('/api/auth/register', registrationData);
      
      setRegistrationSuccess(true);
      setStep(3);
      
    } catch (err) {
      setError(err.response?.data?.message || t('auth.register.errors.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Step 1
  if (step === 1) {
    return (
      <div className="auth-container">
        {/* Language Selector */}
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
          <LanguageSelector />
        </div>
        
        <div className="auth-card">
          <h2>{t('auth.register.title')}</h2>
          <p className="auth-subtitle">{t('auth.register.selectUserType')}</p>
          
          <div className="user-type-selection">
            <div 
              className="user-type-option"
              onClick={() => onUserTypeSelect('bank')}
            >
              <div className="user-type-icon">🏦</div>
              <h3>{t('auth.register.bankOption')}</h3>
              <p>{t('auth.register.bankSubtitle')}</p>
            </div>
            
            <div 
              className="user-type-option"
              onClick={() => onUserTypeSelect('company')}
            >
              <div className="user-type-icon">🏢</div>
              <h3>{t('auth.register.companyOption')}</h3>
              <p>{t('auth.register.companySubtitle')}</p>
            </div>
          </div>
          
          <div className="auth-links">
            <p>
              {t('auth.register.alreadyAccount')}{' '}
              <Link to="/login">{t('auth.register.loginHere')}</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Step 3
  if (step === 3) {
    return (
      <div className="auth-container">
        {/* Language Selector */}
        <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
          <LanguageSelector />
        </div>
        
        <div className="auth-card">
          <h2>{t('auth.register.successTitle')}</h2>
          <div className="success-message">
            <p>{t('auth.register.successMessage')}</p>
            <p>{t('auth.register.pendingApproval')}</p>
            <p>{t('auth.register.emailNotification')}</p>
          </div>
          
          <div className="auth-links" style={{ marginTop: '20px' }}>
            <Link to="/login" className="btn btn-primary btn-block login-btn" style={{ color: 'white' }}>
              {t('auth.register.goToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Registration form
  return (
    <div className="auth-container">
      {/* Language Selector */}
      <div style={{ position: 'absolute', top: '20px', right: '20px', zIndex: 1000 }}>
        <LanguageSelector />
      </div>
      
      <div className="auth-card">
        <h2>{t('auth.register.registerAs', { userType: userType === 'bank' ? t('auth.register.asBank') : t('auth.register.asCompany') })}</h2>
        <p className="auth-subtitle">{t('auth.register.fillInformation')}</p>
        
        {securityWarning && <div className="alert alert-warning">{securityWarning}</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        
        <form onSubmit={onSubmit}>
          <h3>{t('auth.register.personalInfo')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">{t('auth.register.firstName')}</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={onChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName">{t('auth.register.lastName')}</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={onChange}
                required
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">{t('auth.register.emailAddress')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={onChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">{t('auth.register.phoneNumber')}</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={onChange}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="position">{t('auth.register.position')}</label>
            <input
              type="text"
              id="position"
              name="position"
              value={formData.position}
              onChange={onChange}
              required
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">{t('auth.register.password')}</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={onChange}
                required
                minLength="8"
              />
              <small className="form-text text-muted">
                {t('auth.register.passwordRequirements')}
              </small>
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">{t('auth.register.confirmPassword')}</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={onChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
              <label htmlFor="preferredLanguage">{t('auth.register.preferredLanguage') || 'Preferred Language'}</label>
              <select
                id="preferredLanguage"
                name="preferredLanguage"
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                disabled={loading}
                required
                className="form-control"
              >
                <option value="it">Italiano</option>
                <option value="en">English</option>
              </select>
            </div>
          
          <h3>{userType === 'bank' ? t('auth.register.bankInfo') : t('auth.register.companyInfo')}</h3>
          <div className="form-group">
            <label htmlFor="organizationName">{userType === 'bank' ? t('auth.register.bankName') : t('auth.register.companyName')}</label>
            <input
              type="text"
              id="organizationName"
              name="organizationName"
              value={formData.organizationName}
              onChange={onChange}
              required
            />
          </div>

            {userType === 'company' && (
          <div className="form-group">
            <label htmlFor="vatNumber">{t('auth.register.vatNumber')}</label>
            <input
              type="text"
              id="vatNumber"
              name="vatNumber"
              value={formData.vatNumber}
              onChange={onChange}
              required
            />
          </div>
        )}
          
          <div className="form-group">
            <label htmlFor="address.street">{t('auth.register.streetAddress')}</label>
            <input
              type="text"
              id="address.street"
              name="address.street"
              value={formData.organizationAddress.street}
              onChange={onChange}
              required
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="address.city">{t('auth.register.city')}</label>
              <input
                type="text"
                id="address.city"
                name="address.city"
                value={formData.organizationAddress.city}
                onChange={onChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="address.postcode">{t('auth.register.postalCode')}</label>
              <input
                type="text"
                id="address.postcode"
                name="address.postcode"
                value={formData.organizationAddress.postcode}
                onChange={onChange}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="address.country">{t('auth.register.country')}</label>
            <input
              type="text"
              id="address.country"
              name="address.country"
              value={formData.organizationAddress.country}
              onChange={onChange}
              required
            />
          </div>
          
         <div className="form-group checkbox-group">
          <div className="checkbox-label">
            <input
              type="checkbox"
              id="acceptedTerms"
              name="acceptedTerms"
              checked={formData.acceptedTerms}
              onChange={onChange}
              required
            />
            <span>
              {t('auth.register.acceptTerms')}{' '}
              <a
                href={currentLanguage === 'en' ? "/Termini e Condizioni di Utilizzo_eng.pdf" : "/Termini e Condizioni di Utilizzo_ita.pdf"}
                target="_blank"
                rel="noopener noreferrer"
                className="primary-link"
              >
                {t('auth.register.termsConditions')}
              </a>
              {currentLanguage === 'en' ? ' and ' : ' e '}
              <a
                href={currentLanguage === 'en' ? "/Informativa Privacy_eng.pdf" : "/Informativa Privacy_ita.pdf"}
                target="_blank"
                rel="noopener noreferrer"
                className="primary-link"
              >
                {t('auth.register.privacyPolicy')}
              </a>
            </span>
          </div>
        </div>
          
          <div className="form-buttons">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => setStep(1)}
            >
              {t('auth.register.backButton')}
            </button>
            
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading || !!securityWarning}
            >
              {loading ? t('auth.register.registering') : t('auth.register.registerButton')}
            </button>
          </div>
        </form>
        
        <div className="auth-links">
          <p>
            {t('auth.register.alreadyAccount')}{' '}
            <Link to="/login">{t('auth.register.loginHere')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;